import React from 'react';
import * as Yup from 'yup';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, Col, Row, Tooltip } from 'antd';
import { Formik } from 'formik';
import moment from 'moment';

import DatePicker from '../../../common/components/dataEntry/formik/FormikDatePickerField';
import FormikInputNumberField from '../../../common/components/dataEntry/formik/FormikInputNumberField';
import { Counties, Information } from '../../../types';
import API from '../../../api/api';
import config from '../../../config';
import Label from '../../../common/components/dataEntry/components/Label';
import { getAge } from '../helper';

export const informationDefaultValues = {
  zipCode: '',
  householdIncome: 0,
  birthdate: null,
};

const formValidator = Yup.object({
  zipCode: Yup.number().required('Required'),
  householdIncome: Yup.number().required('Required').typeError('Invalid input'),
  birthdate: Yup.date()
    .required('Required')
    .nullable()
    .typeError('Invalid input'),
});

const DATE_FORMAT = 'MM/DD/YYYY';

export interface YourInformationProps {
  counties: Counties | null;
  yourInfoInitialValues: Information | null;
  onYourInformationComplete: (values: Information) => void;
  handleCollapse: () => void;
  nextAccordionEnabled: boolean;
  updateStep: (value: number) => void;
  setLowestCostPlan: (value: number) => void;
}

const YourInformation: React.FC<YourInformationProps> = ({
  counties,
  yourInfoInitialValues,
  onYourInformationComplete,
  handleCollapse,
  nextAccordionEnabled,
  updateStep,
  setLowestCostPlan,
}) => {
  const formInitialValues: Information =
    yourInfoInitialValues ?? informationDefaultValues;

  const onSubmitYourInformation = (values: Information) => {
    getLowestCostPlan(values, counties);
    handleCollapse();
    updateStep(1);
    onYourInformationComplete(values);
  };

  const getLowestCostPlan = async (
    values: Information,
    counties: Counties | null
  ) => {
    const response = await API.post(
      'plans/search',
      {
        filter: {
          metal_level: 'Silver',
        },
        household: {
          income: Number(values.householdIncome),
          has_married_couple: false,
          people: [
            {
              age: getAge(new Date(values.birthdate ?? 0)),
              gender: 'Male',
              uses_tobacco: false,
              is_parent: false,
            },
          ],
        },
        market: 'Individual',
        place: {
          countyfips: counties?.fips,
          state: counties?.state,
          zipcode: counties?.zipcode,
        },
        year: Number(new Date().getFullYear()),
        order: 'asc',
        sort: 'premium',
      },
      {
        params: { apikey: config.API_KEY },
      }
    );

    setLowestCostPlan(response.data.plans[0].premium);
  };

  return (
    <div>
      <Formik
        enableReinitialize={true}
        validationSchema={formValidator}
        initialValues={formInitialValues}
        onSubmit={onSubmitYourInformation}
      >
        {(formik) => (
          <form onSubmit={formik.handleSubmit} autoComplete="off">
            <Label className="formik-field__input-label no-select hand-on-hover ml-16 mb-8 black">
              Your home ZIP Code
            </Label>
            <Row className="ml-16">
              <Col xs={12} sm={6} lg={4} xl={3}>
                <FormikInputNumberField name="zipCode" disabled={true} />
              </Col>
            </Row>
            <Label className="formik-field__input-label no-select hand-on-hover ml-16 mb-8 black">
              {
                <div>
                  What is your estimated household income before taxes?
                  <Tooltip
                    placement="right"
                    title="Enter your estimated total household income before taxes. If you do not know your household income, use your individual salary before taxes."
                  >
                    <ExclamationCircleOutlined className="ml-8" />
                  </Tooltip>
                </div>
              }
            </Label>
            <Row className="ml-16">
              <Col xs={12} sm={6} lg={4} xl={3}>
                <FormikInputNumberField
                  disabled={!nextAccordionEnabled}
                  decimalSeparator="."
                  placeholder={'35,000'}
                  formatter={(value) =>
                    `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                  }
                  name="householdIncome"
                  precision={0}
                />
              </Col>
            </Row>
            <Label className="formik-field__input-label no-select hand-on-hover ml-16 mb-8 black">
              {
                <div>
                  When is your birth date?
                  <Tooltip
                    placement="right"
                    title="Make sure to put the correct year of birth so that the calculation is as accurate as possible"
                  >
                    <ExclamationCircleOutlined className="ml-8" />
                  </Tooltip>
                </div>
              }
            </Label>
            <Row className="ml-16">
              <Col xs={12} sm={6} lg={4} xl={3}>
                <DatePicker
                  allowClear={false}
                  disabled={!nextAccordionEnabled}
                  format={DATE_FORMAT}
                  disabledDate={(d: any) =>
                    !d ||
                    d.isBefore(new Date('01/01/1900')) ||
                    d.isAfter(new Date())
                  }
                  name="birthdate"
                  placeholder={'01/01/1995'}
                  defaultValue={moment(formik.values.birthdate)}
                />
              </Col>
            </Row>
            <Row className="ml-16">
              <Col xs={12} sm={6} lg={4} xl={3}>
                <Button
                  htmlType="submit"
                  type="primary"
                  className="button__large"
                  disabled={
                    !formValidator.isValidSync(formik.values) ||
                    !nextAccordionEnabled
                  }
                >
                  Next
                </Button>
              </Col>
            </Row>
          </form>
        )}
      </Formik>
    </div>
  );
};
export default YourInformation;
